import React from 'react';
import { usePage } from '@inertiajs/react';
// import Layout from '@/Layouts/AuthenticatedLayout';
import {
  User, Film, MessageSquare, ExternalLink, Star, Award
} from 'lucide-react';

const ShowProfile = () => {
  const { profile, scripts, user } = usePage().props;
  
  // Separate featured scripts from other scripts
  const featuredScripts = scripts.filter(script => script.is_featured);
  const regularScripts = scripts.filter(script => !script.is_featured);
  
  // Get writer type and experience level labels
  const writerTypes = {
    'screenwriter': 'Screenwriter',
    'playwright': 'Playwright',
    'tv_writer': 'TV Writer',
    'novelist': 'Novelist adapting work',
    'producer': 'Producer',
    'director': 'Director',
    'other': 'Other',
  };
  
  const experienceLevels = {
    'professional_represented': 'Professional (represented)',
    'professional_independent': 'Professional (independent)',
    'emerging': 'Emerging writer (some credits)',
    'new': 'New writer',
    'student': 'Student',
  };

  return (
    <>
      <div className="max-w-5xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Profile Header */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
          <div className="bg-indigo-700 h-32 w-full"></div>
          <div className="px-6 py-4 relative">
            <div className="absolute -top-16 left-6">
              <div className="h-32 w-32 rounded-full border-4 border-white bg-white overflow-hidden">
                {profile.profile_photo_url ? (
                  <img 
                    src={profile.profile_photo_url} 
                    alt={user.name}
                    className="h-full w-full object-cover" 
                  />
                ) : (
                  <div className="h-full w-full bg-indigo-100 flex items-center justify-center">
                    <User size={64} className="text-indigo-300" />
                  </div>
                )}
              </div>
            </div>
            
            <div className="mt-16">
              <h1 className="text-3xl font-bold text-gray-900">{user.name}</h1>
              
              <div className="mt-2 flex flex-wrap items-center text-sm text-gray-600 gap-x-4 gap-y-2">
                {profile.writer_type && (
                  <div className="flex items-center">
                    <Film size={16} className="mr-1" />
                    {writerTypes[profile.writer_type] || profile.writer_type}
                  </div>
                )}
                
                {profile.experience_level && (
                  <div>
                    {experienceLevels[profile.experience_level] || profile.experience_level}
                  </div>
                )}
              </div>
              
              {profile.bio && (
                <div className="mt-4 text-gray-700">
                  {profile.bio}
                </div>
              )}
              
              {/* Social Links */}
              {profile.social_links && profile.social_links.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {profile.social_links.map((link, index) => (
                    <a 
                      key={index}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-sm text-gray-700 hover:bg-gray-200"
                    >
                      <ExternalLink size={14} className="mr-1" />
                      {link.platform}
                    </a>
                  ))}
                </div>
              )}
            </div>
            
            {/* Contact Button */}
            {profile.allow_messages && (
              <div className="mt-4 flex justify-end">
                <button className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                  <MessageSquare size={18} className="mr-2" /> Message
                </button>
              </div>
            )}
          </div>
        </div>
        
        {/* Featured Scripts Section */}
        {featuredScripts.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Award className="mr-2" size={24} /> Featured Work
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {featuredScripts.map(script => (
                <div key={script.id} className="bg-white rounded-lg shadow-md overflow-hidden border-2 border-indigo-200">
                  {/* Featured Badge */}
                  {script.featured_note && (
                    <div className="bg-indigo-50 border-b border-indigo-100 px-4 py-2">
                      <p className="text-sm text-indigo-700">{script.featured_note}</p>
                    </div>
                  )}
                  
                  <div className="p-4">
                    <div className="flex justify-between items-start">
                      <h3 className="text-lg font-bold text-gray-900">{script.title}</h3>
                      <div className="flex items-center bg-yellow-100 px-2 py-1 rounded text-sm text-yellow-700">
                        <Star size={14} className="mr-1" />
                        {/* Assuming you have a score in your data */}
                        85
                      </div>
                    </div>
                    
                    <div className="mt-2 flex flex-wrap gap-2">
                      {/* {script.genres && script.genres.map((genre, idx) => (
                        <span key={idx} className="px-2 py-1 bg-gray-100 rounded-full text-xs text-gray-700">
                          {genre}
                        </span>
                      ))} */}
                    </div>
                    
                    <div className="mt-3">
                      <h4 className="text-sm font-medium text-gray-700">Logline</h4>
                      <p className="mt-1 text-sm text-gray-600">{script.logline}</p>
                    </div>
                    
                    {script.usp && (
                      <div className="mt-3">
                        <h4 className="text-sm font-medium text-gray-700">Unique Selling Point</h4>
                        <p className="mt-1 text-sm text-gray-600">{script.usp}</p>
                      </div>
                    )}
                    
                    <div className="mt-4">
                      <a 
                        href={route('scripts.show', script.hash)}
                        className="inline-flex items-center px-3 py-1 border border-indigo-500 text-indigo-500 rounded-md hover:bg-indigo-50"
                      >
                        View {script.disclosure_level === 'full' ? 'Full Script' : 'Details'}
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Regular Scripts Section */}
        {regularScripts.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Film className="mr-2" size={24} /> Scripts
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {regularScripts.map(script => (
                <div key={script.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                  <div className="p-4">
                    <h3 className="font-bold text-gray-900">{script.title}</h3>
                    
                    <div className="mt-2 flex flex-wrap gap-1">
                      {script.genres && script.genres.slice(0, 3).map((genre, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-gray-100 rounded-full text-xs text-gray-700">
                          {genre}
                        </span>
                      ))}
                    </div>
                    
                    <div className="mt-2">
                      <p className="text-sm text-gray-600 line-clamp-3">{script.logline}</p>
                    </div>
                    
                    <div className="mt-3">
                      <a 
                        href={route('scripts.show', script.hash)}
                        className="text-sm text-indigo-600 hover:text-indigo-800"
                      >
                        View {script.disclosure_level === 'full' ? 'Full Script' : 'Details'} →
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Empty State */}
        {scripts.length === 0 && (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <Film size={48} className="mx-auto text-gray-400" />
            <h3 className="mt-2 text-lg font-medium text-gray-900">No public scripts</h3>
            <p className="mt-1 text-gray-500">
              This writer hasn't made any scripts public yet.
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default ShowProfile;